import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import * as Sentry from '@sentry/react';
import { BootSplash } from '@/components/BootSplash';
import {
  AUTH_REDIRECT_THRESHOLD,
  AUTH_REDIRECT_WINDOW_MS,
  clearAuthRedirectHistory,
  recordAuthRedirect,
} from '@/lib/authRedirectGuard';
import { clearLegacyAuthToken } from '@/lib/api';
import { canonicalUrl } from '@/lib/hosts';

interface CrossHostNavigateProps {
  /** Absolute path to navigate to (e.g. "/dashboard"). */
  to: string;
  /** When true, replaces the current history entry (default true). */
  replace?: boolean;
}

/**
 * Navigates to `to` on its canonical host:
 *   - same origin → React Router `<Navigate />` (no full reload).
 *   - cross-origin → `window.location.replace(...)` and render `<BootSplash />`.
 *
 * Anti-loop guard: if more than 3 cross-host navigations happen within 5
 * seconds (auth misconfig → AuthGuard ↔ GuestGuard ping-pong), abort the
 * navigation and render a fallback. Prevents browser CPU thrash and gives
 * the user an actionable message instead of a frozen page.
 */
export function CrossHostNavigate({ to, replace = true }: CrossHostNavigateProps) {
  // Accept both bare paths ("/dashboard") and absolute URLs ("https://app.velko-pos.com/dashboard").
  // canonicalUrl only knows how to dispatch bare paths to the right host.
  const isAbsolute = /^https?:\/\//i.test(to);
  const target = isAbsolute ? to : canonicalUrl(to);
  const sameOrigin =
    typeof window !== 'undefined' && new URL(target).origin === window.location.origin;
  const [loopDetected, setLoopDetected] = useState(false);

  useEffect(() => {
    if (sameOrigin) return;
    if (recordAuthRedirect(target)) {
      console.error(
        `[CrossHostNavigate] Auth-redirect loop detected (${AUTH_REDIRECT_THRESHOLD} cross-host nav in ${AUTH_REDIRECT_WINDOW_MS}ms). Aborting navigation to ${target}. ` +
          `Check that the session cookie is shared across .velko-pos.com (SESSION_DOMAIN) and that login creates a web session.`,
      );
      Sentry.captureMessage('auth_redirect_loop', {
        level: 'error',
        tags: {
          target_host: new URL(target).host,
          current_host: window.location.host,
        },
      });
      setLoopDetected(true);
      return;
    }
    if (replace) {
      window.location.replace(target);
    } else {
      window.location.assign(target);
    }
  }, [sameOrigin, target, replace]);

  if (sameOrigin) {
    // Extract pathname+search+hash so React Router does not treat the absolute
    // URL as a relative path (which would yield "/current/http://...").
    try {
      const url = new URL(target);
      const localPath = `${url.pathname}${url.search}${url.hash}` || '/';
      return <Navigate to={localPath} replace={replace} />;
    } catch {
      return <Navigate to={to} replace={replace} />;
    }
  }

  if (loopDetected) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center">
        <h1 className="text-xl font-semibold text-slate-900">Erreur d'authentification</h1>
        <p className="max-w-md text-sm text-slate-600">
          Une boucle de redirection a été détectée. Vérifie que tu acceptes les cookies puis
          rafraîchis la page.
        </p>
        <button
          type="button"
          onClick={() => {
            clearAuthRedirectHistory();
            clearLegacyAuthToken();
            window.location.replace(canonicalUrl('/login'));
          }}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          Revenir à la connexion
        </button>
      </div>
    );
  }

  return <BootSplash />;
}
